class Vertex {
    constructor(x, y, index = -1 // original index in the input array
    ) {
        this.x = x;
        this.y = y;
        this.index = index;
    }
    equals(other) {
        return this.x === other.x && this.y === other.y;
    }
}
// Compute circumcircle of a triangle (center + radius)
function calcCircumCirc(v0, v1, v2) {
    const ax = v0.x, ay = v0.y;
    const bx = v1.x, by = v1.y;
    const cx = v2.x, cy = v2.y;
    const d = 2 * (ax * (by - cy) + bx * (cy - ay) + cx * (ay - by));
    if (Math.abs(d) < 1e-12) {
        // Degenerate (collinear) triangle
        return { c: { x: 0, y: 0 }, r: Infinity };
    }
    const ux = ((ax * ax + ay * ay) * (by - cy) +
        (bx * bx + by * by) * (cy - ay) +
        (cx * cx + cy * cy) * (ay - by)) /
        d;
    const uy = ((ax * ax + ay * ay) * (cx - bx) +
        (bx * bx + by * by) * (ax - cx) +
        (cx * cx + cy * cy) * (bx - ax)) /
        d;
    const r = Math.hypot(ux - ax, uy - ay);
    return { c: { x: ux, y: uy }, r };
}
class Triangle {
    constructor(v0, v1, v2) {
        this.v0 = v0;
        this.v1 = v1;
        this.v2 = v2;
        this.circumCirc = calcCircumCirc(v0, v1, v2);
    }
    inCircumcircle(v) {
        const dx = this.circumCirc.c.x - v.x;
        const dy = this.circumCirc.c.y - v.y;
        return Math.hypot(dx, dy) <= this.circumCirc.r + 1e-9;
    }
}
// Create a large "super triangle" that encloses all points
function superTriangle(vertices) {
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    for (const v of vertices) {
        minX = Math.min(minX, v.x);
        minY = Math.min(minY, v.y);
        maxX = Math.max(maxX, v.x);
        maxY = Math.max(maxY, v.y);
    }
    const dx = (maxX - minX) * 10;
    const dy = (maxY - minY) * 10;
    const v0 = new Vertex(minX - dx, minY - dy * 3);
    const v1 = new Vertex(minX - dx, maxY + dy);
    const v2 = new Vertex(maxX + dx * 3, maxY + dy);
    return new Triangle(v0, v1, v2);
}
// Collect unique edges from a list of triangles (used during point insertion)
function uniqueEdges(edges) {
    const unique = [];
    for (let i = 0; i < edges.length; ++i) {
        let isUnique = true;
        for (let j = 0; j < edges.length; ++j) {
            if (i !== j) {
                const e1 = edges[i];
                const e2 = edges[j];
                if ((e1.v0.equals(e2.v0) && e1.v1.equals(e2.v1)) ||
                    (e1.v0.equals(e2.v1) && e1.v1.equals(e2.v0))) {
                    isUnique = false;
                    break;
                }
            }
        }
        if (isUnique)
            unique.push(edges[i]);
    }
    return unique;
}
// Insert a new vertex into the current triangulation
function addVertex(vertex, triangles) {
    const edges = [];
    // 1. Remove all triangles whose circumcircle contains the new vertex
    triangles = triangles.filter((triangle) => {
        if (triangle.inCircumcircle(vertex)) {
            edges.push({ v0: triangle.v0, v1: triangle.v1 });
            edges.push({ v0: triangle.v1, v1: triangle.v2 });
            edges.push({ v0: triangle.v2, v1: triangle.v0 });
            return false;
        }
        return true;
    });
    // 2. Create new triangles from the unique polygon edges + new vertex
    const unique = uniqueEdges(edges);
    for (const edge of unique) {
        triangles.push(new Triangle(edge.v0, edge.v1, vertex));
    }
    return triangles;
}
// Main Delaunay triangulation (Bowyer-Watson)
function triangulate(vertices) {
    if (vertices.length < 3)
        return [];
    // 1. Create super triangle
    const st = superTriangle(vertices);
    // 2. Start with the super triangle
    let triangles = [st];
    // 3. Insert each point one by one
    for (const vertex of vertices) {
        triangles = addVertex(vertex, triangles);
    }
    // 4. Remove any triangle that touches the super triangle
    triangles = triangles.filter((t) => !(t.v0 === st.v0 ||
        t.v0 === st.v1 ||
        t.v0 === st.v2 ||
        t.v1 === st.v0 ||
        t.v1 === st.v1 ||
        t.v1 === st.v2 ||
        t.v2 === st.v0 ||
        t.v2 === st.v1 ||
        t.v2 === st.v2));
    return triangles;
}
// Public API: Delaunay triangulation given vertices → triangle indices
function delaunayTriangulation(vertices) {
    if (vertices.length < 3)
        return [];
    // Create internal vertices that remember their original index
    const vertList = vertices.map((p, idx) => new Vertex(p.x, p.y, idx));
    const triangles = triangulate(vertList);
    // Convert to flat index array: [i0, j0, k0, i1, j1, k1, ...]
    const indices = [];
    for (const t of triangles) {
        // All vertices in the final triangulation come from the original input
        indices.push(t.v0.index, t.v1.index, t.v2.index);
    }
    return indices;
}
// Example usage:
/*
const vertices: Point[] = [
  { x: 0, y: 0 },
  { x: 10, y: 0 },
  { x: 5, y: 10 },
  { x: 15, y: 5 },
  { x: 2, y: 8 },
  // ... any number of points
];

const triangleIndices = delaunayTriangulation(vertices);

// triangleIndices is a flat array of indices, e.g. [0,1,2, 0,2,4, 1,3,2, ...]
// Use it like this:
for (let i = 0; i < triangleIndices.length; i += 3) {
  const a = vertices[triangleIndices[i]];
  const b = vertices[triangleIndices[i + 1]];
  const c = vertices[triangleIndices[i + 2]];
  console.log(`Triangle: ${a.x},${a.y} → ${b.x},${b.y} → ${c.x},${c.y}`);
}
*/
// This implementation is self-contained, numerically robust (small epsilon), and works with floating-point coordinates.
// It assumes general position (no four points exactly cocircular). For production use with degenerate cases,
// you may want to add a tiny random jitter to the points if needed.
export class Delaunay {
    static triangulate(vertices) {
        return delaunayTriangulation(vertices);
    }
}
//}
